import React, { useState } from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { MapPin, Mail, Phone, Clock, Send, CheckCircle } from 'lucide-react';
export const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    postcode: '',
    appliance: '',
    faultNotes: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[\d\s+()-]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }
    if (!formData.postcode.trim()) {
      newErrors.postcode = 'Postcode is required';
    }
    if (!formData.appliance) {
      newErrors.appliance = 'Please select an appliance type';
    }
    if (!formData.faultNotes.trim()) {
      newErrors.faultNotes = 'Please describe the fault';
    } else if (formData.faultNotes.trim().length < 10) {
      newErrors.faultNotes = 'Please provide more details (at least 10 characters)';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setSubmitted(true);
      // Form submission logic would go here
      console.log('Form submitted:', formData);
    }
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };
  const services = [{
    title: 'Washing Machines',
    description: 'Complete diagnostics and repairs for all washing machine faults—drum issues, leaks, drainage problems and more.'
  }, {
    title: 'Dishwashers',
    description: "Expert repairs for dishwashers that won't drain, clean properly, or complete cycles. We fix pump and heating element faults."
  }, {
    title: 'Tumble Dryers',
    description: "Repairs for dryers that won't heat, stop mid-cycle, or make unusual noises. We handle all major brands."
  }];
  const faqs = [{
    question: 'How do I book a repair?',
    answer: 'Simply email or call us. We normally schedule engineer visits within 24–48 hours of your initial contact.'
  }, {
    question: 'What appliances do you handle?',
    answer: 'We service most household appliances, including washing machines, tumble dryers, dishwashers, cookers, fridges and TVs (subject to age and condition).'
  }];
  return <div className="min-h-screen bg-slate-50" style={{
    width: '100%'
  }} data-id="element-300">
      <Header data-id="element-301" />
      <section className="py-20 bg-gradient-to-br from-teal-600 to-cyan-700 text-white" data-id="element-302">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-303">
          <h1 className="text-5xl font-bold mb-6" data-id="element-304">Contact Us</h1>
          <p className="text-xl text-teal-50 max-w-3xl" data-id="element-305">
            Your local appliance repair team—friendly, efficient and reliable
          </p>
        </div>
      </section>
      <section className="py-16" data-id="element-306">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-307">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-308">
            <div data-id="element-309">
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-id="element-310">
                <h2 className="text-2xl font-bold text-slate-800 mb-6" data-id="element-311">
                  Get in Touch
                </h2>
                <p className="text-slate-600 mb-8" data-id="element-312">
                  Whether you need help now or want to set up an annual repair
                  service, we're here to support you.
                </p>
                <div className="space-y-6" data-id="element-313">
                  <div className="flex items-start space-x-4" data-id="element-314">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-315">
                      <MapPin className="w-6 h-6 text-teal-600" data-id="element-316" />
                    </div>
                    <div data-id="element-317">
                      <h3 className="font-semibold text-slate-800 mb-1" data-id="element-318">
                        Address
                      </h3>
                      <p className="text-slate-600" data-id="element-319">112-116 The Broadway</p>
                      <p className="text-slate-600" data-id="element-320">London, NW7 3TB</p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4" data-id="element-321">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-322">
                      <Mail className="w-6 h-6 text-teal-600" data-id="element-323" />
                    </div>
                    <div data-id="element-324">
                      <h3 className="font-semibold text-slate-800 mb-1" data-id="element-325">
                        Email
                      </h3>
                      <a href="mailto:Benjobalolong082@gmail.com" className="text-teal-600 hover:underline break-all" data-id="element-326">
                        Benjobalolong082@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4" data-id="element-327">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-328">
                      <Phone className="w-6 h-6 text-teal-600" data-id="element-329" />
                    </div>
                    <div data-id="element-330">
                      <h3 className="font-semibold text-slate-800 mb-1" data-id="element-331">
                        Phone
                      </h3>
                      <a href="tel:08000554969" className="text-teal-600 hover:underline text-xl font-semibold" data-id="element-332">
                        0800 055 4969
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4" data-id="element-333">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-334">
                      <Clock className="w-6 h-6 text-teal-600" data-id="element-335" />
                    </div>
                    <div data-id="element-336">
                      <h3 className="font-semibold text-slate-800 mb-3" data-id="element-337">
                        Opening Hours
                      </h3>
                      <div className="space-y-1 text-slate-600" data-id="element-338">
                        <p data-id="element-339">Monday–Friday: 9:00am–5:00pm</p>
                        <p data-id="element-340">Saturday–Sunday: Closed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl shadow-lg p-8" data-id="element-341">
                <h3 className="text-xl font-bold text-slate-800 mb-4" data-id="element-342">
                  Appliances We Repair
                </h3>
                <div className="space-y-3" data-id="element-343">
                  {services.map((service, index) => <div key={index} className="border-l-4 border-teal-600 pl-4 py-2" data-id="element-344">
                      <h4 className="font-semibold text-slate-800 mb-1" data-id="element-345">
                        {service.title}
                      </h4>
                      <p className="text-sm text-slate-600" data-id="element-346">
                        {service.description}
                      </p>
                    </div>)}
                </div>
              </div>
            </div>
            <div data-id="element-347">
              <div className="bg-white rounded-xl shadow-lg p-8 mb-8" data-id="element-348">
                <h2 className="text-2xl font-bold text-slate-800 mb-6" data-id="element-349">
                  Request a Repair
                </h2>
                <p className="text-slate-600 mb-6" data-id="element-350">
                  Fill in the form below and we'll get back to you within 24
                  hours to arrange your repair visit.
                </p>
                {submitted ? <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-lg" data-id="element-351">
                    <div className="flex items-start space-x-3" data-id="element-352">
                      <CheckCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-1" data-id="element-353" />
                      <div data-id="element-354">
                        <h3 className="font-semibold text-slate-800 mb-2" data-id="element-355">
                          Thank you for your request!
                        </h3>
                        <p className="text-slate-600" data-id="element-356">
                          We've received your repair request and will contact
                          you within 24 hours to arrange your engineer visit.
                        </p>
                      </div>
                    </div>
                  </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-357">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-358">
                      <div data-id="element-359">
                        <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-360">
                          Full Name *
                        </label>
                        <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${errors.name ? 'border-red-500' : 'border-slate-300'}`} data-id="element-361" />
                        {errors.name && <p className="mt-1 text-sm text-red-600" data-id="element-362">
                            {errors.name}
                          </p>}
                      </div>
                      <div data-id="element-363">
                        <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-364">
                          Email Address *
                        </label>
                        <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${errors.email ? 'border-red-500' : 'border-slate-300'}`} data-id="element-365" />
                        {errors.email && <p className="mt-1 text-sm text-red-600" data-id="element-366">
                            {errors.email}
                          </p>}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-367">
                      <div data-id="element-368">
                        <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-369">
                          Phone Number *
                        </label>
                        <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${errors.phone ? 'border-red-500' : 'border-slate-300'}`} data-id="element-370" />
                        {errors.phone && <p className="mt-1 text-sm text-red-600" data-id="element-371">
                            {errors.phone}
                          </p>}
                      </div>
                      <div data-id="element-372">
                        <label htmlFor="postcode" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-373">
                          Postcode *
                        </label>
                        <input type="text" id="postcode" name="postcode" value={formData.postcode} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${errors.postcode ? 'border-red-500' : 'border-slate-300'}`} data-id="element-374" />
                        {errors.postcode && <p className="mt-1 text-sm text-red-600" data-id="element-375">
                            {errors.postcode}
                          </p>}
                      </div>
                    </div>
                    <div data-id="element-376">
                      <label htmlFor="appliance" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-377">
                        Appliance Type *
                      </label>
                      <select id="appliance" name="appliance" value={formData.appliance} onChange={handleChange} className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${errors.appliance ? 'border-red-500' : 'border-slate-300'}`} data-id="element-378">
                        <option value="" data-id="element-379">Select an appliance</option>
                        <option value="washing-machine" data-id="element-380">Washing Machine</option>
                        <option value="dishwasher" data-id="element-381">Dishwasher</option>
                        <option value="tumble-dryer" data-id="element-382">Tumble Dryer</option>
                        <option value="cooker" data-id="element-383">Cooker/Oven</option>
                        <option value="fridge-freezer" data-id="element-384">Fridge/Freezer</option>
                        <option value="tv" data-id="element-385">TV/Set-Top Box</option>
                      </select>
                      {errors.appliance && <p className="mt-1 text-sm text-red-600" data-id="element-386">
                          {errors.appliance}
                        </p>}
                    </div>
                    <div data-id="element-387">
                      <label htmlFor="faultNotes" className="block text-sm font-medium text-slate-700 mb-2" data-id="element-388">
                        Fault Description *
                      </label>
                      <textarea id="faultNotes" name="faultNotes" rows={4} value={formData.faultNotes} onChange={handleChange} placeholder="Please describe the problem with your appliance..." className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent ${errors.faultNotes ? 'border-red-500' : 'border-slate-300'}`} data-id="element-389" />
                      {errors.faultNotes && <p className="mt-1 text-sm text-red-600" data-id="element-390">
                          {errors.faultNotes}
                        </p>}
                    </div>
                    <button type="submit" className="w-full bg-teal-600 text-white px-8 py-4 rounded-lg hover:bg-teal-700 transition-colors font-semibold text-lg flex items-center justify-center space-x-2" data-id="element-391">
                      <span data-id="element-392">Submit Request</span>
                      <Send className="w-5 h-5" data-id="element-393" />
                    </button>
                  </form>}
              </div>
              <div className="bg-white rounded-xl shadow-lg p-8" data-id="element-394">
                <h3 className="text-xl font-bold text-slate-800 mb-4" data-id="element-395">
                  Common Questions
                </h3>
                <div className="space-y-4" data-id="element-396">
                  {faqs.map((faq, index) => <div key={index} className="border-l-4 border-teal-600 pl-4 py-2" data-id="element-397">
                      <h4 className="font-semibold text-slate-800 mb-1" data-id="element-398">
                        {faq.question}
                      </h4>
                      <p className="text-sm text-slate-600" data-id="element-399">{faq.answer}</p>
                    </div>)}
                </div>
                <div className="mt-6" data-id="element-400">
                  <a href="/faq" className="text-teal-600 hover:underline font-medium" data-id="element-401">
                    View all FAQs →
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer data-id="element-402" />
    </div>;
};